import React, { Component, ErrorInfo } from 'react';
import Cookies from 'js-cookie';
import { UserRole } from '../globalContant';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  isChunkError: boolean;
}

// Style objects
const errorBoundaryStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100vh',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '2rem',
  textAlign: 'center',
  backgroundColor: '#f8f9fa',
};

const errorActionsStyles: React.CSSProperties = {
  marginTop: '1.5rem',
  display: 'flex',
  gap: '1rem',
};

const errorDetailsStyles: React.CSSProperties = {
  margin: '1rem 0',
  maxWidth: '600px',
  textAlign: 'left',
};

const errorPreStyles: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  backgroundColor: '#f1f1f1',
  padding: '1rem',
  borderRadius: '4px',
};

const buttonStyles: React.CSSProperties = {
  padding: '0.5rem 1rem',
  backgroundColor: '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '1rem',
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      isChunkError: error.name === 'ChunkLoadError',
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught in Error Boundary:', error, errorInfo);
  }

  handleReload = () => {
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');
    window.location.href = window.location.origin;
  };

  handleGoToDashboard = () => {
    window.location.href = '/dashboard';
  };

  renderErrorContent() {
    const { error, isChunkError } = this.state;
    const userType = Cookies.get('userType');

    if (this.props.fallback) {
      return this.props.fallback;
    }

    if (isChunkError) {
      return (
        <div style={errorBoundaryStyles}>
          <h1>Application Update Required</h1>
          <p>We've released a new version of the application. Please reload to get the latest features and fixes.</p>
          <div style={errorActionsStyles}>
            <button style={buttonStyles} onClick={this.handleReload}>
              Reload Application
            </button>
            {userType === UserRole.ADMIN && (
              <button style={buttonStyles} onClick={this.handleGoToDashboard}>
                Continue to Dashboard
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={errorBoundaryStyles}>
        <h1>Something Went Wrong</h1>
        <p>We're sorry for the inconvenience. Please try again later.</p>
        <div style={errorDetailsStyles}>
          <details>
            <summary>Error Details</summary>
            <pre style={errorPreStyles}>{error?.toString()}</pre>
          </details>
        </div>
        <div style={errorActionsStyles}>
          <button style={buttonStyles} onClick={this.handleReload}>
            Try Again
          </button>
          <button style={buttonStyles} onClick={this.handleGoToDashboard}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      return this.renderErrorContent();
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
